/* Модель экранов «Текста» (APP-SPEC-5 §7): режимы формы, подписи, единицы цены, чего не хватает.
   Всё, что уходит в заказ, — только ключи опубликованной формы (fields / screen_fields); подписи — словарь интерфейса. */
import type { StudioForm } from '../../studio/studio-api';
import { record } from '../../studio/studio-content';
import { toolField, toolFields, type ToolField } from '../../studio/circle/tool-form';
import { PROMPT_MAX } from '../../studio/prompt-limit';

export const textModes = ['transcription', 'product_description', 'prompt_create', 'prompt_from_photo', 'prompt_from_video', 'improve'] as const;
export type TextMode = (typeof textModes)[number];
/** Вид заказа экрана (как kindNow() прототипа): у расшифровки — файл или ссылка, у промпта из фото — фото или ролик */
export type TextKind = 'stt' | 'yt' | 'seo' | 'pphoto' | 'pvideo' | 'pnew' | 'pfix';
export type TextPolicy = Record<string, unknown> & { mode: TextMode };

export function textPolicy(form: StudioForm): TextPolicy | null {
  const stt = form.constraints?.transcription;
  if (stt?.policy_version === 'transcription-v1') return { ...stt, mode: 'transcription' };
  const policy = form.constraints?.text;
  if (policy?.policy_version !== 'text-n8n-v1' || !textModes.includes(policy.mode as TextMode)) return null;
  // «Промпт из видео» существует только при флаге политики (03-api §9.2: pvideo)
  if (policy.mode === 'prompt_from_video' && policy.prompt_from_video_available !== true) return null;
  return { ...policy, mode: policy.mode as TextMode };
}
export const textTitles: Record<TextMode, string> = {
  transcription: 'Текст из аудио и видео',
  product_description: 'Описание товара',
  prompt_create: 'Создать промпт',
  prompt_from_photo: 'Промпт из фото',
  prompt_from_video: 'Промпт из видео',
  improve: 'Улучшить промпт',
};
export const kindTitles: Record<TextKind, string> = {
  stt: 'Текст из аудио и видео',
  yt: 'Текст из аудио и видео',
  seo: 'Описание товара',
  pphoto: 'Промпт из фото',
  pvideo: 'Промпт из видео',
  pnew: 'Создать промпт',
  pfix: 'Улучшить промпт',
};
/** Глагол кнопки (VERB прототипа) */
export const verbs: Record<TextKind, string> = {
  stt: 'Получить текст',
  yt: 'Получить текст',
  seo: 'Создать описание',
  pphoto: 'Создать промпт',
  pvideo: 'Создать промпт',
  pnew: 'Создать промпт',
  pfix: 'Улучшить промпт',
};
/** Единица цены в шапке (UNIT прототипа), если сервер не назвал свою */
export const unitFallback: Record<TextKind, string> = {
  stt: 'C в минуту',
  yt: 'C за текст ролика',
  seo: 'C за описание',
  pphoto: 'C за промпт',
  pvideo: 'C за промпт',
  pnew: 'C за варианты',
  pfix: 'C за варианты',
};
export const inlineKind = (kind: TextKind) => kind === 'pnew' || kind === 'pfix';

export const fieldLabels: Record<string, string> = {
  text: 'Текст',
  wishes: 'Пожелания',
  kind: 'Для чего',
  tone: 'Стиль текста',
  market: 'Площадка',
  style: 'Стиль промпта',
  lang: 'Язык промпта',
  language: 'Язык записи',
  diarization: 'Кто говорит',
  timestamps: 'Время реплик',
};
export const choiceLabels: Record<string, string> = {
  photo: 'Фото',
  video: 'Видео',
  music: 'Музыка',
  text: 'Текст',
  sell: 'Продающий',
  expert: 'Экспертный',
  neutral: 'Нейтральный',
  prem: 'Премиальный',
  friend: 'Дружелюбный',
  short: 'Короткий',
  gift: 'Подарочный',
  seo: 'Для поиска',
  wb: 'Wildberries',
  ozon: 'Ozon',
  ym: 'Яндекс Маркет',
  avito: 'Avito',
  site: 'Свой сайт',
  detail: 'Подробный',
  list: 'По пунктам',
  ru: 'Русский',
  en: 'English',
  es: 'Español',
  de: 'Deutsch',
  fr: 'Français',
  auto: 'Определить автоматически',
  src: 'Как в ролике',
  Russian: 'Русский',
  English: 'English',
  Spanish: 'Español',
  German: 'Deutsch',
  French: 'Français',
  full: 'Весь текст',
  sum: 'Пересказ',
  th: 'Тезисы',
  true: 'Да',
  false: 'Нет',
};
/** Пояснения строк выбора листа (choice: название и пояснение) */
export const choiceNotes: Record<string, string> = {
  full: 'С временем абзацев',
  sum: 'Главное в нескольких абзацах',
  th: 'Коротко по пунктам',
  wb: 'Общая для товарных инструментов',
  ozon: 'Общая для товарных инструментов',
  ym: 'Общая для товарных инструментов',
  avito: 'Только для описания',
  site: 'Только для описания',
};
export const promptStyleNotes: Record<string, [string, string]> = {
  short: ['Короткий', 'Одна-две фразы'],
  detail: ['Подробный', 'Сцена, свет, камера, стиль'],
  list: ['По пунктам', 'Ключ и значение строками'],
};
/** Тон «Описания товара» у ленты: подпись (у short — «Коротко»), образец заголовка и фразы стиля и метка (TONES прототипа).
    Образцы — оформление карточки стиля, а не данные пользователя. */
export const toneCards: Record<string, { name: string; head: string; line: string; badge?: string }> = {
  sell: { name: 'Продающий', head: 'Тишина и 40 часов музыки', line: 'Шумоподавление выключает город, заряда хватит на неделю', badge: 'Хит' },
  expert: { name: 'Экспертный', head: 'Bluetooth 5.3, ANC до 35 дБ', line: 'Кодеки AAC и SBC, 10 минут заряда дают 4 часа музыки' },
  neutral: { name: 'Нейтральный', head: 'Беспроводные наушники', line: 'Шумоподавление, до 40 часов работы, мягкие амбушюры' },
  prem: { name: 'Премиальный', head: 'Звук, который слышно в деталях', line: 'Эко-кожа, мягкая посадка и тишина вокруг' },
  friend: { name: 'Дружелюбный', head: 'Город станет тише', line: 'Наденьте и слушайте любимое хоть всю неделю' },
  short: { name: 'Коротко', head: 'Тихо. Долго. Удобно', line: '40 часов, шумоподавление, мягкие амбушюры' },
  gift: { name: 'Подарочный', head: 'Подарок на каждый день', line: 'В коробке кабель и чехол, можно дарить сразу', badge: 'Новое' },
  seo: { name: 'Для поиска', head: 'Наушники беспроводные с шумоподавлением', line: 'Накладные, с микрофоном, складные, для телефона' },
};
export const featureChips = ['Материал', 'Размеры', 'Цвет', 'Комплект', 'Уход', 'Гарантия'];
export const wishChips: Array<[string, string]> = [
  ['Коротко', 'коротко и по делу'],
  ['Простыми словами', 'простыми словами, без терминов'],
  ['Подробно', 'подробно, с деталями'],
  ['Без эмодзи', 'без эмодзи'],
];
export const ideaChips: Array<[string, string]> = [
  ['Кот-астронавт', 'Рыжий кот-астронавт сидит на Луне и смотрит на Землю'],
  ['Город будущего', 'Ночной город будущего, летающие машины и неоновые вывески'],
  ['Уютное кафе', 'Уютное кафе у окна в дождливое утро'],
  ['Горы на рассвете', 'Горное озеро на рассвете, туман над водой'],
  ['Портрет в студии', 'Студийный портрет девушки в мягком свете'],
];
/** «для фото» — подпись над вариантами */
export const forWords: Record<string, string> = { photo: 'фото', video: 'видео', music: 'музыки', text: 'текста' };

/** Значения по умолчанию берём только из опубликованной формы. */
export function textDefaults(form: StudioForm) {
  const values: Record<string, string> = {};
  for (const field of form.fields) {
    const raw = record(field).default_value;
    if (typeof raw === 'string' && (!field.values.length || field.values.includes(raw))) values[field.key] = raw;
  }
  return values;
}
export function selectedActions(value: string | undefined): string[] {
  try {
    const items: unknown = JSON.parse(value || '[]');
    return Array.isArray(items) && items.length <= 20 && items.every(v => typeof v === 'string' && v.length <= 120) ? items : [];
  } catch {
    return [];
  }
}
export function toggleAction(current: string | undefined, choice: string): string {
  let values = selectedActions(current);
  if (values.includes(choice)) values = values.filter(v => v !== choice);
  else {
    if (choice === 'Подробнее' || choice === 'Короче') values = values.filter(v => v !== 'Подробнее' && v !== 'Короче');
    values.push(choice);
  }
  return JSON.stringify(values);
}

const size = (value: string | undefined) => Array.from(value || '').length;
const num = (value: unknown, fallback: number) => (typeof value === 'number' && Number.isFinite(value) ? value : fallback);

/** Ссылка на ролик YouTube: youtube.com/watch?v=… (и m., www.), youtu.be/…, shorts — иначе пусто (ytId прототипа). */
export function youtubeId(raw: string): string {
  let value = raw.trim();
  if (!value) return '';
  if (!/^https?:\/\//i.test(value)) value = `https://${value}`;
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return '';
  }
  const host = url.hostname.toLowerCase().replace(/^(www|m)\./, '');
  const ok = (id: string | null | undefined) => (/^[\w-]{11}$/.test(id || '') ? id! : '');
  if (host === 'youtu.be') return ok(url.pathname.slice(1).split('/')[0]);
  if (host === 'youtube.com' && url.pathname === '/watch') return ok(url.searchParams.get('v'));
  if (host === 'youtube.com' && /^\/shorts\//.test(url.pathname)) return ok(url.pathname.split('/')[2]);
  return '';
}

/** Ключи полей экрана по форме: что опубликовано, то и есть. */
export function textKeys(form: StudioForm | null | undefined) {
  const policy = form ? textPolicy(form) : null;
  const uploads = toolFields(form).filter(field => field.upload);
  const photos = uploads.filter(field => field.upload?.kind === 'photo');
  const key = (...keys: string[]) => toolField(form, ...keys)?.key;
  const stt = policy?.mode === 'transcription';
  return {
    audio: uploads.find(field => field.upload?.kind === 'audio')?.key,
    video: uploads.find(field => field.upload?.kind === 'video')?.key,
    photos: photos.map(field => field.key),
    /* ссылка YouTube — только если политика публикует youtube и форма — поле ссылки (03-api §9.2: url, kind, lang) */
    url: stt && (policy?.youtube === true || policy?.youtube_available === true) ? key('url', 'youtube_url') : undefined,
    ytKind: stt ? key('result', 'yt_kind', 'kind') : undefined,
    ytLang: stt ? key('text_lang', 'yt_lang', 'lang') : undefined,
    language: stt ? key('language') : key('language'),
    diarization: key('diarization', 'speakers'),
    timestamps: key('timestamps'),
    text: key('text', 'idea', 'prompt'),
    name: key('name'),
    features: key('features'),
    tone: key('tone'),
    market: key('market'),
    wishes: key('wishes', 'own'),
    kind: key('kind', 'for'),
    actions: key('actions', 'templates'),
    style: key('style'),
    lang: stt ? undefined : key('lang'),
    from: key('from_ms', 'trim_from_ms', 'start_ms'),
  };
}
export type TextKeys = ReturnType<typeof textKeys>;

/** Вид заказа по форме и входам */
export function textKind(policy: TextPolicy, inputs: Record<string, string>, keys: TextKeys): TextKind {
  switch (policy.mode) {
    case 'transcription':
      return keys.url && inputs[keys.url] && !(keys.audio && inputs[keys.audio]) && !(keys.video && inputs[keys.video]) ? 'yt' : 'stt';
    case 'product_description':
      return 'seo';
    case 'prompt_from_video':
      return 'pvideo';
    case 'prompt_from_photo':
      return policy.prompt_from_video_available === true && keys.video && inputs[keys.video] ? 'pvideo' : 'pphoto';
    case 'prompt_create':
      return 'pnew';
    default:
      return 'pfix';
  }
}
/** Пределы поля: из политики, иначе из поля формы */
export function textLimits(policy: TextPolicy, field: ToolField | undefined, kind: TextKind) {
  const max = num(policy.text_max_length, field?.max_length ?? (kind === 'pfix' || kind === 'pnew' ? PROMPT_MAX : kind === 'seo' ? 1120 : 500));
  const min = num(policy.text_min_length, kind === 'pfix' ? 10 : kind === 'pnew' ? 5 : 0);
  return { min, max: Math.max(1, Math.min(max, field?.max_length ?? max)) };
}
export function wishesLimit(policy: TextPolicy, field: ToolField | undefined, fallback = 120) {
  return Math.max(1, Math.min(num(policy.wishes_max_length, fallback), field?.max_length ?? fallback));
}
/** «Название и преимущества»: первая строка — название, ниже — преимущества (seoParse прототипа) */
export function splitProduct(value: string) {
  const lines = value.replace(/\r/g, '').split('\n');
  return { name: (lines[0] || '').trim(), features: lines.slice(1).join('\n').replace(/^\n+|\s+$/g, '') };
}
/** Чип «Материал»: дописывает строку «Материал: », повторное нажатие убирает её */
export function featureOn(value: string, label: string) {
  return value.split('\n').some(line => line.trim().toLowerCase().startsWith(`${label.toLowerCase()}:`));
}
export function toggleFeature(value: string, label: string) {
  if (featureOn(value, label))
    return value
      .split('\n')
      .filter(line => !line.trim().toLowerCase().startsWith(`${label.toLowerCase()}:`))
      .join('\n');
  return `${value}${value && !value.endsWith('\n') ? '\n' : ''}${label}: `;
}
/** Чип-добавка пожеланий: дописать фразу через запятую, повторно — убрать */
export function toggleWish(value: string, phrase: string) {
  const parts = value
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);
  const has = parts.some(part => part.toLowerCase() === phrase.toLowerCase());
  return (has ? parts.filter(part => part.toLowerCase() !== phrase.toLowerCase()) : [...parts, phrase]).join(', ');
}
export const wishOn = (value: string, phrase: string) => value.split(',').some(part => part.trim().toLowerCase() === phrase.toLowerCase());

/** Чего не хватает для создания: подпись неактивной кнопки (missText прототипа); null — можно. */
export function textMissing(form: StudioForm, inputs: Record<string, string>, rights: boolean, extra: { recording?: boolean; link?: string } = {}): string | null {
  const policy = textPolicy(form);
  if (!policy || !form.available) return 'Создание сейчас недоступно';
  const keys = textKeys(form);
  const kind = textKind(policy, inputs, keys);
  const uploads = toolFields(form).filter(field => field.upload);
  if (uploads.some(field => inputs[field.key]) && !rights) return 'Подтвердите права на материалы';
  if (kind === 'stt' || kind === 'yt') {
    if (extra.recording) return 'Остановите запись';
    const files = Number(!!(keys.audio && inputs[keys.audio])) + Number(!!(keys.video && inputs[keys.video]));
    if (files > 1) return 'Выберите аудио или видео';
    if (!files) {
      if (keys.url && (inputs[keys.url] || extra.link)) return youtubeId(inputs[keys.url] || extra.link || '') ? null : 'Это не ссылка на ролик YouTube';
      return keys.url ? 'Загрузите запись или видео, или вставьте ссылку' : 'Загрузите запись или видео';
    }
  } else if (kind === 'pphoto') {
    if (!keys.photos.some(key => inputs[key])) return 'Загрузите фото';
  } else if (kind === 'pvideo') {
    if (!(keys.video && inputs[keys.video])) return 'Загрузите видео';
  } else if (kind === 'seo') {
    const value = keys.text ? inputs[keys.text] || '' : [inputs[keys.name || ''] || '', inputs[keys.features || ''] || ''].join('\n');
    const { max } = textLimits(policy, toolField(form, keys.text || keys.features || ''), kind);
    if (size(splitProduct(value).name) < 2) return 'Напишите название товара первой строкой';
    if (size(value) > max) return `Текст длиннее ${max} знаков`;
  } else {
    const { min, max } = textLimits(policy, toolField(form, keys.text || ''), kind);
    const length = size((inputs[keys.text || ''] || '').trim());
    if (length < Math.max(1, min)) return `Напишите от ${Math.max(1, min)} знаков`;
    if (length > max) return `Текст длиннее ${max} знаков`;
  }
  if (keys.wishes) {
    const limit = wishesLimit(policy, toolField(form, keys.wishes), kind === 'seo' ? 300 : 120);
    if (size(inputs[keys.wishes]) > limit) return `Пожелания длиннее ${limit} знаков`;
  }
  for (const field of form.fields) {
    if (field.upload || field.key === 'consent_accepted') continue;
    if (field.required && !inputs[field.key]?.trim()) return 'Заполните, что нужно для создания';
  }
  return null;
}

/** Склонение: 1 реплика, 2 реплики, 5 реплик */
export function plural(count: number, one: string, few: string, many: string) {
  const n = Math.abs(count) % 100,
    d = n % 10;
  return n > 10 && n < 20 ? many : d === 1 ? one : d >= 2 && d <= 4 ? few : many;
}
export function mmss(ms: number) {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
export function fileSize(bytes: number) {
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1).replace('.', ',').replace(/,0$/, '')} МБ`;
  return `${Math.max(1, Math.round(bytes / 1024))} КБ`;
}
const FORMAT: Record<string, string> = {
  'audio/mpeg': 'MP3',
  'audio/wav': 'WAV',
  'audio/mp4': 'M4A',
  'audio/ogg': 'OGG',
  'audio/webm': 'WebM',
  'video/mp4': 'MP4',
  'video/quicktime': 'MOV',
  'image/jpeg': 'JPG',
  'image/png': 'PNG',
  'image/webp': 'WebP',
};
const joinOr = (items: string[]) => (items.length > 1 ? `${items.slice(0, -1).join(', ')} или ${items[items.length - 1]}` : items[0] || '');
/** Строка ограничений рамки по политике полей: «MP3, WAV или M4A до 20 МБ и 5:00 · MP4 или MOV до 100 МБ и 10:00» */
export function limitsNote(fields: ToolField[]) {
  return fields
    .filter(field => field.upload)
    .map(field => {
      const upload = field.upload!;
      const names = [...new Set(upload.accept.map(type => FORMAT[type] || type.split('/')[1]?.toUpperCase() || type))];
      const parts = [upload.max_bytes ? `${Math.round(upload.max_bytes / 1048576)} МБ` : '', upload.max_duration_ms ? mmss(upload.max_duration_ms) : ''].filter(Boolean);
      return `${joinOr(names)}${parts.length ? `, до ${parts.join(' и ')}` : ''}`;
    })
    .join(' · ');
}
/** Текст для полей описания других экранов (clipText прототипа): не длиннее max, режется по последней фразе */
export function clipText(value: string, max: number) {
  const text = value.replace(/\n+/g, '. ').replace(/\.\s*\./g, '.');
  if (Array.from(text).length <= max) return text;
  const cut = Array.from(text).slice(0, max).join('');
  const end = cut.lastIndexOf('. ');
  return end > max * 0.5 ? cut.slice(0, end + 1) : `${cut.replace(/\s+\S*$/, '')}…`;
}
